import React, { useMemo, useState } from 'react';
import {
  CircularProgress,
  ListItemText,
  MenuItem,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

import { DealerSearchInput } from 'app/components/ReusableUIComponents/LowLevelComponents/DealerSearchInput';
import {
  MakeItem,
  filterMakesBySearch,
  getMakeDisplayName,
} from 'app/components/ReusableUIComponents/LowLevelComponents/makeSearchUtils';
import {
  HeaderDropdownMAIQ,
  HeaderDropdownMenuItemMAIQ,
  HEADER_DROPDOWN_LIGHT_TRIGGER_SX,
} from 'app/components/ReusableUIComponents/LowLevelComponents/HeaderDropdownMAIQ';

interface HeaderMakeSelectProps {
  makesList: MakeItem[];
  selectedMake: MakeItem | null;
  disabled?: boolean;
  loading?: boolean;
  onSelect: (make: MakeItem) => void;
  className?: string;
}

const isMakeSelected = (selectedMake: MakeItem | null, item: MakeItem) =>
  selectedMake?.make_id === item.make_id;

export const HeaderMakeSelect: React.FC<HeaderMakeSelectProps> = ({
  makesList,
  selectedMake,
  disabled = false,
  loading = false,
  onSelect,
  className,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const open = Boolean(anchorEl);

  const filteredMakes = useMemo(
    () => filterMakesBySearch(makesList, searchTerm),
    [makesList, searchTerm],
  );

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled || loading) return;
    setAnchorEl(event.currentTarget);
    setSearchTerm('');
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchTerm('');
  };

  const handleSelect = (make: MakeItem) => {
    onSelect(make);
    handleClose();
  };

  const triggerLabel = loading ? (
    <>
      <CircularProgress size={16} sx={{ mr: 1 }} />
      Loading makes...
    </>
  ) : selectedMake ? (
    getMakeDisplayName(selectedMake)
  ) : (
    'Select make'
  );

  return (
    <HeaderDropdownMAIQ
      id="header-make-select"
      className={className}
      label={triggerLabel}
      richLabel={loading}
      open={open}
      anchorEl={anchorEl}
      onOpen={handleOpen}
      onClose={handleClose}
      disabled={disabled || loading}
      minWidth={320}
      menuMinWidth={320}
      menuMaxHeight={400}
      menuVariant="dealer"
      triggerSx={HEADER_DROPDOWN_LIGHT_TRIGGER_SX}
      aria-label="Select make"
    >
      <DealerSearchInput
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="Search makes..."
      />
      {filteredMakes.length > 0 ? (
        filteredMakes.map(item => {
          const selected = isMakeSelected(selectedMake, item);
          return (
            <HeaderDropdownMenuItemMAIQ
              key={item.make_id}
              selected={selected}
              onClick={() => handleSelect(item)}
            >
              <ListItemText
                sx={{ minWidth: 0 }}
                primary={
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {item.make_name}
                  </Typography>
                }
                secondary={item.manufacturer_name}
              />
              {selected ? (
                <CheckIcon
                  sx={{ fontSize: 18, color: 'primary.main', ml: 1 }}
                />
              ) : null}
            </HeaderDropdownMenuItemMAIQ>
          );
        })
      ) : searchTerm ? (
        <MenuItem disabled>
          <ListItemText primary="No makes found" />
        </MenuItem>
      ) : (
        <MenuItem disabled>
          <ListItemText primary="No makes available" />
        </MenuItem>
      )}
    </HeaderDropdownMAIQ>
  );
};

export default HeaderMakeSelect;
